import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { colors } from '../../base/ui/Tokens';
import { twilioVoiceService } from '../services/TwilioVoiceService';
export default function VcoAudioControls({ role, roomName }) {
    const owner = useMemo(() => ({ role, roomName }), [role, roomName]);
    const currentOwner = useRef(owner);
    currentOwner.current = owner;
    const mounted = useRef(true);
    const pending = useRef(null);
    const [snapshot, setSnapshot] = useState(() => twilioVoiceService.getVcoStatus(owner));
    const [phone, setPhone] = useState(() => twilioVoiceService.getCallStatus());
    const [busy, setBusy] = useState(false);
    const [actionError, setActionError] = useState();
    const state = snapshot.roomName === roomName && snapshot.role === role
        ? snapshot : twilioVoiceService.getVcoStatus(owner);
    useEffect(() => {
        mounted.current = true;
        pending.current = null;
        setBusy(false);
        setActionError(undefined);
        const onStatus = (next) => {
            if (currentOwner.current !== owner || next.roomName !== roomName || next.role !== role)
                return;
            setSnapshot(next);
            setActionError(undefined);
        };
        const onPhone = (next) => { if (currentOwner.current === owner)
            setPhone(next); };
        twilioVoiceService.on('vcoStatusChange', onStatus);
        twilioVoiceService.on('statusChange', onPhone);
        onStatus(twilioVoiceService.getVcoStatus(owner));
        onPhone(twilioVoiceService.getCallStatus());
        return () => {
            mounted.current = false;
            twilioVoiceService.off('vcoStatusChange', onStatus);
            twilioVoiceService.off('statusChange', onPhone);
        };
    }, [owner, role, roomName]);
    const runAction = useCallback(async (action) => {
        if (pending.current || currentOwner.current !== owner)
            return;
        pending.current = owner;
        setBusy(true);
        setActionError(undefined);
        try {
            await action();
        }
        catch (error) {
            if (mounted.current && currentOwner.current === owner) {
                setActionError(error instanceof Error ? error.message : 'VCO audio could not be confirmed. Try again.');
            }
        }
        finally {
            if (mounted.current && currentOwner.current === owner && pending.current === owner) {
                pending.current = null;
                setBusy(false);
            }
        }
    }, [owner]);
    const disconnected = state.status === 'failed' || state.status === 'idle';
    const selected = role === 'consumer' ? state.muted : state.speaking;
    const ending = Boolean(phone.hangupPending);
    const retryEnd = ending && phone.status === 'failed';
    const unavailable = busy || (ending ? !retryEnd : disconnected ? false : state.status !== 'connected' || state.controlPending);
    const statusText = ending ? retryEnd ? 'Phone audio needs End confirmation' : 'Ending phone audio…'
        : state.status === 'connected' ? 'VCO voice connected'
            : state.status === 'failed' ? 'VCO voice unavailable'
                : state.status === 'idle' ? 'VCO voice disconnected' : 'Connecting VCO voice…';
    const error = actionError || (ending ? phone.error : state.error);
    const action = ending ? () => twilioVoiceService.hangupCall()
        : disconnected ? () => twilioVoiceService.joinVcoBridge(owner)
            : role === 'consumer' ? () => twilioVoiceService.setVcoMuted(!state.muted)
                : () => twilioVoiceService.setInterpreterSpeaking(!state.speaking);
    const label = ending ? retryEnd ? 'Retry End' : 'Ending…'
        : busy || state.controlPending && !disconnected ? 'Checking audio…'
            : disconnected ? 'Reconnect voice'
                : role === 'consumer' ? state.muted ? 'Resume voice' : 'Mute voice'
                    : state.speaking ? 'Stop speaking to phone' : 'Speak to phone';
    return (React.createElement("section", { "aria-label": "VCO audio", style: { display: 'grid', gap: 8, color: '#fff', maxWidth: 300, width: '100%' } },
        React.createElement("div", { "aria-live": "polite", role: "status", style: { background: colors.primary01, borderRadius: 8, padding: 10, fontSize: 14 } },
            statusText,
            error ? React.createElement("div", { style: { marginTop: 6 } }, error) : null),
        React.createElement("button", { "aria-busy": busy || state.controlPending, "aria-pressed": ending || disconnected ? undefined : selected, disabled: unavailable, onClick: () => runAction(action), style: { background: selected || retryEnd ? colors.error05 : colors.primary06, color: '#fff',
                border: '1px solid rgba(255,255,255,.35)', borderRadius: 8, fontSize: 16, fontWeight: 700,
                minHeight: 48, padding: '10px 14px', opacity: unavailable ? 0.55 : 1, cursor: unavailable ? 'default' : 'pointer' }, type: "button" }, label)));
}
